// src/pages/client-tabs/ClientInvoicesTab.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiArrowUp,
  FiArrowDown,
  FiMoreHorizontal,
  FiFileText,
  FiSend,
  FiEdit2,
  FiTrash2,
  FiRefreshCcw,
  FiCalendar,
} from 'react-icons/fi';

import api from '../../api/axios';
import { sendInvoice, voidInvoice, writeOffInvoice } from '../../services/invoiceService';

/* ----------------------------- helpers ----------------------------- */

const fmtMoney = (n, c = 'USD') =>
  (typeof n === 'number' ? n : Number(n || 0)).toLocaleString(undefined, {
    style: 'currency',
    currency: c || 'USD',
  });

// UTC-safe date-only
const fmtDateOnlyUTC = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : '—';

const resolveId = (row) => row?.invoiceId || row?._id || row?.id;

const STATUS_OPTIONS = [
  { label: 'Unpaid', value: 'unpaid' },
  { label: 'Partial', value: 'partial' },
  { label: 'Paid', value: 'paid' },
  { label: 'Overdue', value: 'overdue' },
];

const LIFECYCLE_OPTIONS = [
  { label: 'Draft', value: 'draft' },
  { label: 'Approved', value: 'approved' },
  { label: 'Sent', value: 'sent' },
  { label: 'Void', value: 'void' },
  { label: 'Written Off', value: 'written_off' },
];

// chips
const STATUS_CHIP = {
  unpaid: 'bg-amber-100 text-amber-700 border border-amber-200',
  partial: 'bg-blue-100 text-blue-700 border border-blue-200',
  paid: 'bg-green-100 text-green-700 border border-green-200',
  overdue: 'bg-rose-100 text-rose-700 border border-rose-200',
};

const LIFECYCLE_CHIP = {
  draft: 'bg-slate-100 text-slate-800 border border-slate-200',
  approved: 'bg-indigo-100 text-indigo-800 border border-indigo-200',
  sent: 'bg-purple-100 text-purple-800 border border-purple-200',
  void: 'bg-zinc-100 text-zinc-800 border border-zinc-200',
  written_off: 'bg-orange-100 text-orange-800 border border-orange-200',
};

const SORTABLE = [
  { id: 'createdAt', label: 'Created' },
  { id: 'updatedAt', label: 'Updated' },
  { id: 'issueDate', label: 'Issue Date' },
  { id: 'dueDate', label: 'Due Date' },
  { id: 'invoiceNumber', label: 'Invoice #' },
  { id: 'totalAmount', label: 'Total' },
  { id: 'balanceDue', label: 'Balance' },
];

/* ----------------------------- portal action menu ----------------------------- */

function ActionMenuPortal({
  anchorEl,
  row,
  canMutate,
  onClose,
  onView,
  onOpenPdf,
  onQuickEdit,
  onVoid,
  onWriteOff,
  onDelete,
}) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ top: 0, left: 0, origin: 'top-right' });

  useLayoutEffect(() => {
    if (!anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const vh = window.innerHeight;

    const menuW = 240;
    const gap = 8;

    let left = r.right - menuW;
    if (left < 8) left = Math.max(8, r.left);

    let top = r.bottom + gap;
    let origin = 'top-right';
    setStyle({ top, left, origin });

    requestAnimationFrame(() => {
      const el = ref.current;
      if (!el) return;
      const h = el.offsetHeight;
      if (top + h > vh - 8) {
        top = Math.max(8, r.top - h - gap);
        origin = 'bottom-right';
      }
      setStyle({ top, left, origin });
    });
  }, [anchorEl]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!ref.current) return;
      if (ref.current.contains(e.target)) return;
      if (anchorEl && anchorEl.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === 'Escape' && onClose();
    const onScrollOrResize = () => onClose();

    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [anchorEl, onClose]);

  const id = resolveId(row);
  const locked = ['void', 'written_off', 'sent'].includes(
    String(row?.lifecycleStatus || '').toLowerCase()
  );

  return createPortal(
    <div
      ref={ref}
      role="menu"
      style={{ position: 'fixed', top: style.top, left: style.left, zIndex: 50 }}
      className={`min-w-[240px] rounded-xl border border-slate-200 bg-white shadow-xl origin-${style.origin} transform transition duration-100`}
    >
      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50"
        onClick={() => {
          onClose();
          onView(id);
        }}
      >
        View details
      </button>

      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
        onClick={async () => {
          onClose();
          await onOpenPdf(id);
        }}
      >
        <FiFileText /> Open PDF
      </button>

      <button
        role="menuitem"
        className="w-full px-3 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
        onClick={() => {
          onClose();
          if (locked) onView(id);
          else onQuickEdit(row);
        }}
      >
        <FiEdit2 /> {locked ? 'View' : 'Edit'}
      </button>

      <div className="my-1 h-px bg-slate-100" />

      {/* Send intentionally removed from menu */}

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left ${
          canMutate ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'
        }`}
        onClick={() => {
          onClose();
          onVoid(row);
        }}
      >
        VOID
      </button>

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left ${
          canMutate ? 'hover:bg-slate-50' : 'opacity-40 cursor-not-allowed'
        }`}
        onClick={() => {
          onClose();
          onWriteOff(row);
        }}
      >
        Write-off
      </button>

      <button
        role="menuitem"
        disabled={!canMutate}
        className={`w-full px-3 py-2 text-left text-rose-600 ${
          canMutate ? 'hover:bg-rose-50' : 'opacity-40 cursor-not-allowed'
        }`}
        onClick={() => {
          onClose();
          onDelete(row);
        }}
      >
        <FiTrash2 className="inline -mt-0.5 mr-1" /> Archive
      </button>
    </div>,
    document.body
  );
}

/* ----------------------------- main: client-scoped tab ----------------------------- */

const ClientInvoicesTab = ({
  invoices = [],
  canEdit = false,
  setEditingInvoice,
  onDeleteInvoice,
  fetchInvoices,
}) => {
  const navigate = useNavigate();

  // Query state (simplified filter bar)
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [lifecycleStatus, setLifecycleStatus] = useState('all');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');

  // Pagination (page size fixed; no UI control now)
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  // Reset pagination when knobs change
  useEffect(() => {
    setPage(1);
  }, [search, status, lifecycleStatus, sortBy, sortDir]);

  const resetFilters = () => {
    setSearch('');
    setStatus('all');
    setLifecycleStatus('all');
    setSortBy('createdAt');
    setSortDir('desc');
    setPage(1);
  };

  /* ----------------------------- filtering & sorting ----------------------------- */

  const filtered = useMemo(() => {
    let rows = Array.isArray(invoices) ? invoices.slice() : [];

    // search (invoiceNumber, notes)
    const q = search.trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) => {
        const num = String(r.invoiceNumber || '').toLowerCase();
        const notes = String(r.notes || '').toLowerCase();
        return num.includes(q) || notes.includes(q);
      });
    }

    // status
    if (status !== 'all') {
      rows = rows.filter((r) => String(r.status || '').toLowerCase() === status);
    }

    // lifecycle
    if (lifecycleStatus !== 'all') {
      rows = rows.filter(
        (r) => String(r.lifecycleStatus || '').toLowerCase() === lifecycleStatus
      );
    }

    // sort
    rows.sort((a, b) => {
      const av = a?.[sortBy];
      const bv = b?.[sortBy];

      let cmp = 0;
      if (av == null && bv != null) cmp = -1;
      else if (av != null && bv == null) cmp = 1;
      else if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else if (!isNaN(Date.parse(av)) && !isNaN(Date.parse(bv))) cmp = new Date(av) - new Date(bv);
      else cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });

      return sortDir === 'asc' ? cmp : -cmp;
    });

    return rows;
  }, [invoices, search, status, lifecycleStatus, sortBy, sortDir]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filtered.length / limit)),
    [filtered.length, limit]
  );

  const pageRows = useMemo(() => {
    const start = (page - 1) * limit;
    return filtered.slice(start, start + limit);
  }, [filtered, page, limit]);

  /* ----------------------------- actions ----------------------------- */

  const openPdf = useCallback(async (id) => {
    try {
      if (!id) throw new Error('Missing invoice id');

      const resp = await api.get(`/invoices/${id}/pdf`, {
        responseType: 'blob',
        params: { download: 1, v: Date.now() },
      });

      const blob = resp?.data;
      const ct = String(resp?.headers?.['content-type'] || '').toLowerCase();

      if (ct.includes('application/json')) {
        const text = await blob.text?.();
        try {
          const json = JSON.parse(text || '{}');
          throw new Error(json?.error || 'Server error while generating PDF');
        } catch {
          throw new Error(text || 'Server error while generating PDF');
        }
      }

      if (!(blob instanceof Blob) || blob.size === 0) throw new Error('Empty PDF response');

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      toast.error(`PDF error: ${e?.message || 'Unable to download PDF'}`);
    }
  }, []);

  const doSend = async (row) => {
    if (!canEdit) return;
    const suggested = row?.clientSnapshot?.email || row?.client?.email || '';
    const to = prompt('Send to (comma-separated emails):', suggested);
    if (to === null) return;

    try {
      await sendInvoice(resolveId(row), {
        to: to
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        subject: `Invoice ${row.invoiceNumber}`,
        message: '',
        attachPdf: true,
        force: true,
      });
      toast.success(`Sent invoice ${row.invoiceNumber}`);
      fetchInvoices?.();
    } catch {
      toast.error('Failed to send invoice');
    }
  };

  const doVoid = async (row) => {
    if (!canEdit) return;
    const reason = prompt('Reason for void (optional):', '');
    if (reason === null) return;
    try {
      await voidInvoice(resolveId(row), { reason });
      toast.success('Invoice voided');
      fetchInvoices?.();
    } catch {
      toast.error('Failed to void invoice');
    }
  };

  const doWriteOff = async (row) => {
    if (!canEdit) return;
    const reason = prompt('Reason for write-off (required):', '');
    if (!reason?.trim()) {
      toast.info('Write-off cancelled (reason required).');
      return;
    }
    try {
      await writeOffInvoice(resolveId(row), { reason });
      toast.success('Invoice written off');
      fetchInvoices?.();
    } catch {
      toast.error('Failed to write off invoice');
    }
  };

  const doDelete = async (row) => {
    if (!canEdit) return;
    const id = resolveId(row);
    if (!id) return;
    if (!window.confirm('Archive this invoice?\nIf payments exist, deletion is blocked.')) return;
    try {
      await onDeleteInvoice?.(id);
      toast.success('Invoice archived');
    } catch {
      toast.error('Failed to archive invoice');
    }
  };

  /* ----------------------------- helpers ----------------------------- */

  const isLocked = (row) =>
    ['void', 'written_off', 'sent'].includes(String(row?.lifecycleStatus || '').toLowerCase());

  const SortHeader = ({ id, label }) => {
    const active = sortBy === id;
    return (
      <button
        type="button"
        onClick={() => {
          if (sortBy === id) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
          else {
            setSortBy(id);
            setSortDir('asc');
          }
          setPage(1);
        }}
        className={`inline-flex items-center gap-1 ${
          active ? 'text-gray-900' : 'text-gray-600 hover:text-gray-900'
        }`}
      >
        {label}
        {active ? (sortDir === 'asc' ? <FiArrowUp /> : <FiArrowDown />) : null}
      </button>
    );
  };

  /* ----------------------------- render ----------------------------- */

  return (
    <div className="w-full">
      {/* Simplified Filters Bar */}
      <div className="mb-4">
        <div className="rounded-xl bg-white border border-slate-200 shadow-sm p-3 overflow-x-auto">
          <div className="flex gap-2 items-center min-w-max">
            {/* Search */}
            <div className="relative w-64">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search invoice # or notes…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="w-full pl-9 pr-3 py-2 text-sm border rounded-xl bg-white focus:outline-none focus:ring-4 focus:ring-blue-100"
              />
            </div>

            {/* Status */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                title="Filter by status"
              >
                {['all', ...STATUS_OPTIONS.map((s) => s.value)].map((s) => (
                  <option key={s} value={s}>
                    {s === 'all' ? 'All Statuses' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Lifecycle */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={lifecycleStatus}
                onChange={(e) => {
                  setLifecycleStatus(e.target.value);
                  setPage(1);
                }}
                title="Filter by lifecycle"
              >
                {['all', ...LIFECYCLE_OPTIONS.map((s) => s.value)].map((s) => (
                  <option key={s} value={s}>
                    {s === 'all'
                      ? 'All Lifecycles'
                      : s === 'written_off'
                      ? 'Written Off'
                      : s.charAt(0).toUpperCase() + s.slice(1)}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Sort by */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setPage(1);
                }}
                title="Sort by"
              >
                {SORTABLE.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.label}
                  </option>
                ))}
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Sort direction */}
            <div className="relative">
              <select
                className="pl-3 pr-8 py-2 text-sm border rounded-xl bg-white appearance-none"
                value={sortDir}
                onChange={(e) => {
                  setSortDir(e.target.value);
                  setPage(1);
                }}
                title="Sort direction"
              >
                <option value="desc">Newest</option>
                <option value="asc">Oldest</option>
              </select>
              <FiChevronDown className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
            </div>

            {/* Reset */}
            <button
              className="flex items-center gap-2 px-3 py-2 text-sm border rounded-xl bg-white hover:bg-slate-50"
              onClick={resetFilters}
              title="Reset filters"
            >
              <FiRefreshCcw /> Reset
            </button>
          </div>
        </div>
      </div>

      {/* Table Card */}
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur">
              <tr className="text-xs uppercase tracking-wider text-slate-600">
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="invoiceNumber" label="Invoice #" />
                </th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="issueDate" label="Issue" />
                </th>
                <th className="px-4 py-3 text-left font-bold">
                  <SortHeader id="dueDate" label="Due" />
                </th>
                <th className="px-4 py-3 text-left font-bold">Status</th>
                <th className="px-4 py-3 text-left font-bold">Lifecycle</th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="totalAmount" label="Total" />
                </th>
                <th className="px-4 py-3 text-right font-bold">
                  <SortHeader id="balanceDue" label="Balance" />
                </th>
                <th className="px-4 py-3 text-right font-bold">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {pageRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-14">
                    <div className="text-center">
                      <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                        <FiFileText className="text-slate-400" />
                      </div>
                      <div className="text-slate-600 font-semibold">No invoices found</div>
                      <div className="text-slate-500 text-sm">Adjust filters to see results.</div>
                    </div>
                  </td>
                </tr>
              ) : (
                pageRows.map((row) => {
                  const id = resolveId(row);
                  const locked = isLocked(row);
                  return (
                    <tr key={id} className="group transition hover:bg-blue-50/40">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button
                          className="font-semibold text-blue-700 hover:underline"
                          onClick={() => navigate(`/invoices/${id}`)}
                          title="View details"
                        >
                          {row.invoiceNumber}
                        </button>
                        <div className="text-xs text-slate-500">{row.currency || 'USD'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDateOnlyUTC(row.issueDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{fmtDateOnlyUTC(row.dueDate)}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_CHIP[row.status] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1) || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            LIFECYCLE_CHIP[row.lifecycleStatus] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.lifecycleStatus === 'written_off'
                            ? 'Written Off'
                            : row.lifecycleStatus?.charAt(0).toUpperCase() +
                                row.lifecycleStatus?.slice(1) || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {fmtMoney(row.totalAmount, row.currency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right">
                        {fmtMoney(row.balanceDue, row.currency)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex justify-end">
                          <button
                            className="p-2 rounded hover:bg-slate-100"
                            onClick={(e) => {
                              setMenuRow(row);
                              setMenuAnchor(e.currentTarget);
                            }}
                            aria-haspopup="menu"
                            aria-expanded={menuRow && resolveId(menuRow) === id ? 'true' : 'false'}
                            title="Actions"
                          >
                            <FiMoreHorizontal />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-slate-100">
          {pageRows.length === 0 ? (
            <div className="p-6 text-center text-slate-500">No invoices.</div>
          ) : (
            pageRows.map((row) => {
              const id = resolveId(row);
              const locked = isLocked(row);
              return (
                <div key={id} className="p-4 hover:bg-blue-50/60 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">
                        <button
                          className="hover:underline"
                          onClick={() => navigate(`/invoices/${id}`)}
                          title="View details"
                        >
                          {row.invoiceNumber}
                        </button>
                      </div>
                      <div className="text-sm text-slate-600 flex items-center gap-2">
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> Issue {fmtDateOnlyUTC(row.issueDate)}
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <FiCalendar /> Due {fmtDateOnlyUTC(row.dueDate)}
                        </span>
                      </div>
                      <div className="mt-1 flex gap-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            STATUS_CHIP[row.status] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.status?.charAt(0).toUpperCase() + row.status?.slice(1) || '—'}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                            LIFECYCLE_CHIP[row.lifecycleStatus] ||
                            'bg-slate-100 text-slate-700 border border-slate-200'
                          }`}
                        >
                          {row.lifecycleStatus === 'written_off'
                            ? 'Written Off'
                            : row.lifecycleStatus?.charAt(0).toUpperCase() +
                                row.lifecycleStatus?.slice(1) || '—'}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold">{fmtMoney(row.totalAmount, row.currency)}</div>
                      <div className="text-xs text-slate-500">
                        Balance {fmtMoney(row.balanceDue, row.currency)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={() => navigate(`/invoices/${id}`)}
                    >
                      View
                    </button>

                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={async () => {
                        await openPdf(id);
                      }}
                    >
                      <FiFileText className="inline -mt-0.5 mr-1" />
                      PDF
                    </button>

                    <button
                      className="px-3 py-1.5 rounded-xl border text-sm"
                      onClick={() => {
                        if (locked) navigate(`/invoices/${id}`);
                        else setEditingInvoice?.(row);
                      }}
                    >
                      <FiEdit2 className="inline -mt-0.5 mr-1" />
                      {locked ? 'View' : 'Edit'}
                    </button>

                    {canEdit && (
                      <>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm"
                          onClick={() => doSend(row)}
                        >
                          <FiSend className="inline -mt-0.5 mr-1" />
                          Send
                        </button>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm"
                          onClick={() => doVoid(row)}
                        >
                          VOID
                        </button>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm"
                          onClick={() => doWriteOff(row)}
                        >
                          WO
                        </button>
                        <button
                          className="px-3 py-1.5 rounded-xl border text-sm text-rose-600 border-rose-200"
                          onClick={() => doDelete(row)}
                        >
                          <FiTrash2 className="inline -mt-0.5 mr-1" />
                          Archive
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 bg-slate-50 border-t flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-sm text-slate-600">
            Page <span className="font-semibold">{page}</span> of{' '}
            <span className="font-semibold">{totalPages}</span> •{' '}
            <span className="font-semibold">{filtered.length}</span> total
          </div>
          <div className="flex items-center gap-2">
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              title="Previous"
            >
              <FiChevronLeft />
            </button>
            <span className="text-sm text-slate-700">
              Page <span className="font-semibold">{page}</span> of{' '}
              <span className="font-semibold">{totalPages}</span>
            </span>
            <button
              className="p-2 rounded-xl border bg-white disabled:opacity-40"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              title="Next"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {/* Portaled action menu */}
      {menuAnchor && menuRow && (
        <ActionMenuPortal
          anchorEl={menuAnchor}
          row={menuRow}
          canMutate={canEdit}
          onClose={() => {
            setMenuAnchor(null);
            setMenuRow(null);
          }}
          onView={(id) => navigate(`/invoices/${id}`)}
          onOpenPdf={openPdf}
          onQuickEdit={(row) => setEditingInvoice?.(row)}
          onVoid={doVoid}
          onWriteOff={doWriteOff}
          onDelete={doDelete}
        />
      )}
    </div>
  );
};

export default ClientInvoicesTab;
